import { useRef, useState, useMemo } from 'react';

import {
  transformRegionsIntoLocationsArray,
  generateMarkersString,
} from '@virginexperiencedays/components-v2/src/layout/Maps/generateMapMarker';
import { useElementClientWidth } from '@virginexperiencedays/components-v2/src/layout/Maps/useElementClientWidth';
import { reduce as groupLocationsByRegion } from '@virginexperiencedays/components-v2/src/pages/product/Locations/reduce';
import type { Regions } from '@virginexperiencedays/components-v2/src/pages/product/Locations';
import type { Location } from '@virginexperiencedays/products';

const CONTAINER_HEIGHT = 400;

export const useLocations = (locations: Location[], GmapApiKey: string) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(true);
  const [mapState, setMapState] = useState({
    isOpenModal: false,
    activeRegion: '',
    isStatic: true,
  });

  const { width } = useElementClientWidth({ ref: mapContainerRef });
  const regions: Regions = groupLocationsByRegion(locations);

  const regionNames = useMemo(() => {
    if (!Object.keys(regions).length) return [];
    return Object.keys(regions);
  }, [regions]);

  const locationsArray = useMemo(() => {
    return transformRegionsIntoLocationsArray(regions, mapState.activeRegion);
  }, [regions, mapState.activeRegion]);

  const markersString = generateMarkersString(locationsArray);
  const staticImageUrl =
    width > 0
      ? `https://maps.googleapis.com/maps/api/staticmap?size=${width}x${CONTAINER_HEIGHT}&${markersString}&key=${GmapApiKey}`
      : null;

  // Update handlers to modify specific fields
  const handleStateChange = (key: keyof typeof mapState, value: any) => {
    setMapState((prevState) => ({ ...prevState, [key]: value }));
  };

  const handleOpenChange = (value: boolean) => {
    handleStateChange('isOpenModal', value);
  };

  const handleRegionClick = (region: string) => {
    handleStateChange('activeRegion', region);
    handleStateChange('isStatic', false);
    if (mapState.isOpenModal) {
      handleStateChange('isOpenModal', false);
    }
  };

  return {
    mapContainerRef,
    isOpen,
    mapState,
    width,
    containerHeight: CONTAINER_HEIGHT,
    regions,
    regionNames,
    setIsOpen,
    locationsArray,
    staticImageUrl,
    handleStateChange,
    handleOpenChange,
    handleRegionClick,
  } as const;
};
